import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Textarea,
  Button,
  Stack,
  Divider,
  LinearProgress,
  Grid,
  IconButton,
  Alert,
  CircularProgress,
  Select,
  Option,
  Checkbox,
  Chip,
  ChipDelete,
} from '@mui/joy';
import {
  Plus as AddIcon,
  Trash as DeleteIcon,
  PencilSimple as EditIcon,
  CloudArrowUp as UploadIcon,
  FloppyDisk as SaveIcon,
} from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { setOrganizationTimezone } from '../../lib/timezoneUtils';
import { GoogleFontsSelect } from '../../components/GoogleFontsSelect';
import { TopPageModal } from '../../components/TopPageModal';
import { ServiceAreaModal } from '../../components/ServiceAreaModal';
import { ColorPicker } from '../../components/cms/ColorPicker';
import { ManufacturerSelector } from '../../components/common/ManufacturerSelector';
import { AddressAutocompleteInput } from '../../components/common/AddressAutocompleteInput';
import { PageHeader } from '../../components/common/PageHeader';
import { useOrganizationUsers } from '../../hooks/useOrganizationUsers';
import { changedFields, organizationFieldLabels, brandingFieldLabels, requiredOrganizationFields, requiredFieldErrors, organizationSaveErrors, fieldLabel } from './organizationForm';
import { applyPrefill } from '../../utils/prefill';
import type { Organization, TopPage, OrganizationOverview, AreaServed, ParsedAddress } from '../../types';

/**
 * The org payload carries the outgoing-mail signature and the suggestion built
 * from the company profile. Declared here rather than on the shared
 * `Organization` type, which this page does not own.
 */
type OrganizationForm = Partial<Organization> & {
  default_email_signature?: string;
  suggested_email_signature?: string;
};

export const OrganizationPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<OrganizationForm>({});
  const [brandingData, setBrandingData] = useState<Partial<OrganizationOverview>>({});
  const orgBaseline = useRef<OrganizationForm>({});
  const brandingBaseline = useRef<Partial<OrganizationOverview>>({});
  const errorSummary = useRef<HTMLDivElement>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState('');
  const [topPageModalOpen, setTopPageModalOpen] = useState(false);
  const [editingTopPage, setEditingTopPage] = useState<TopPage | null>(null);
  const [serviceAreaModalOpen, setServiceAreaModalOpen] = useState(false);
  const [editingServiceArea, setEditingServiceArea] = useState<AreaServed | null>(null);
  const [logoUploadError, setLogoUploadError] = useState<string | null>(null);
  const [selectedManufacturers, setSelectedManufacturers] = useState<number[]>([]);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [customServiceInput, setCustomServiceInput] = useState('');
  const [editCoordinates, setEditCoordinates] = useState(false);
  const { users: organizationUsers } = useOrganizationUsers();

  // Predefined services grouped by category
  const PREDEFINED_SERVICES: Record<string, string[]> = {
    'Residential Roofing': [
      'Emergency Services',
      'Residential Roof Replacement',
      'Roof Rejuvenation',
      'Residential Roof Repairs',
      'Residential Roof Maintenance',
      'Residential Roof Installation',
      'Storm and Hail Damage',
    ],
    'Commercial Roofing': [
      'Commercial Roof Replacement',
      'Commercial Roof Repairs',
      'Commercial Roof Installation',
      'Commercial Roof Maintenance',
    ],
  };

  const allPredefinedServices = Object.values(PREDEFINED_SERVICES).flat();
  const customServices = selectedServices.filter(
    (s) => !allPredefinedServices.includes(s)
  );

  // Fetch organization data
  const { data: orgData, isLoading: orgLoading, isError: orgError, refetch: refetchOrg } = useQuery({
    queryKey: ['organization'],
    queryFn: async () => {
      const response = await api.get<OrganizationForm>('/api/organizations/current/');
      return response.data;
    },
  });

  // Fetch organization overview (branding)
  const { data: overviewData, isLoading: overviewLoading } = useQuery({
    queryKey: ['organization-overview'],
    queryFn: async () => {
      const response = await api.get<OrganizationOverview>('/api/marketing/organization-overview/');
      return response.data;
    },
  });

  // Fetch top pages
  const { data: topPagesData, isLoading: topPagesLoading } = useQuery({
    queryKey: ['top-pages'],
    queryFn: async () => {
      const response = await api.get<any>('/api/top-pages/');
      console.log('Top pages API response:', response.data);
      const data = response.data;
      // Handle paginated response
      if (Array.isArray(data)) {
        return data as TopPage[];
      } else if (data?.results && Array.isArray(data.results)) {
        return data.results as TopPage[];
      }
      return [];
    },
  });

  // Fetch service areas
  const { data: serviceAreasData, isLoading: serviceAreasLoading } = useQuery({
    queryKey: ['areas-served'],
    queryFn: async () => {
      const response = await api.get<any>('/api/areas-served/');
      const data = response.data;
      // Handle paginated response
      if (Array.isArray(data)) {
        return data as AreaServed[];
      } else if (data?.results && Array.isArray(data.results)) {
        return data.results as AreaServed[];
      }
      return [];
    },
  });

  // Update mutations
  const updateOrgMutation = useMutation({
    mutationFn: async (data: OrganizationForm): Promise<Organization> => {
      const response = await api.patch<Organization>('/api/organizations/current/', data);
      return response.data;
    },
    onSuccess: (updatedData: Organization) => {
      // Update timezone in localStorage if it was changed
      if (updatedData.time_zone) {
        setOrganizationTimezone(updatedData.time_zone);
        queryClient.invalidateQueries({ queryKey: ['organization-timezone'] });
      }
    },
  });

  const updateBrandingMutation = useMutation({
    mutationFn: async (data: Partial<OrganizationOverview>) => {
      if (overviewData?.id) {
        const response = await api.patch(`/api/marketing/organization-overview/${overviewData.id}/`, data);
        return response.data;
      } else {
        const response = await api.post('/api/marketing/organization-overview/', data);
        return response.data;
      }
    },
  });

  const createTopPageMutation = useMutation({
    mutationFn: async (data: Omit<TopPage, 'id'>) => {
      const response = await api.post('/api/top-pages/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['top-pages'] });
    },
  });

  const updateTopPageMutation = useMutation({
    mutationFn: async ({ id, ...data }: TopPage) => {
      const response = await api.patch(`/api/top-pages/${id}/`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['top-pages'] });
    },
  });

  const deleteTopPageMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/top-pages/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['top-pages'] });
    },
  });

  const createServiceAreaMutation = useMutation({
    mutationFn: async (data: Omit<AreaServed, 'id'>) => {
      const response = await api.post('/api/areas-served/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['areas-served'] });
    },
  });

  const updateServiceAreaMutation = useMutation({
    mutationFn: async ({ id, ...data }: AreaServed) => {
      const response = await api.patch(`/api/areas-served/${id}/`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['areas-served'] });
    },
  });

  const deleteServiceAreaMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/areas-served/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['areas-served'] });
    },
  });

  // Initialize form data when data loads
  useEffect(() => {
    if (orgData) {
      // The account holder's name and a signature block built from the company
      // profile are already on record from signup, so the author and signature
      // boxes open filled in rather than blank. Seeded only where the org has
      // saved nothing, and editable like any other field here.
      const next = applyPrefill(orgData, {
        authorship_first_name: orgData.first_name,
        authorship_last_name: orgData.last_name,
        default_email_signature: orgData.suggested_email_signature,
      });
      const previous = orgBaseline.current;
      const switchedOrganization = Boolean(previous.id && previous.id !== next.id);
      setFormData(current => switchedOrganization ? next : ({ ...next, ...changedFields(current, previous, organizationFieldLabels) }));
      orgBaseline.current = next;
      // Store organization timezone for timezone utilities
      if (orgData.time_zone) {
        setOrganizationTimezone(orgData.time_zone);
      }
      // Initialize selected manufacturers from org data
      if (orgData.manufacturers) {
        setSelectedManufacturers(current => switchedOrganization || JSON.stringify(current) === JSON.stringify(previous.manufacturers || []) ? orgData.manufacturers! : current);
      }
      // Initialize selected services from org data
      if (orgData.services) {
        setSelectedServices(current => switchedOrganization || JSON.stringify(current) === JSON.stringify(previous.services || []) ? orgData.services! : current);
      }
    }
  }, [orgData]);

  useEffect(() => {
    if (overviewData) {
      const previous = brandingBaseline.current;
      setBrandingData(current => previous.id && previous.id !== overviewData.id ? overviewData : ({ ...overviewData, ...changedFields(current, previous, brandingFieldLabels) }));
      brandingBaseline.current = overviewData;
    }
  }, [overviewData]);


  // Calculate Knowledge Graph progress
  const knowledgeGraphScore = useMemo(() => {
    const fields = [
      // Basic info (required fields always filled)
      formData.name,
      formData.email,
      formData.phone,
      formData.company_email,
      formData.company_phone_number,
      formData.main_office_address_1,
      formData.main_office_city,
      formData.main_office_state,
      formData.main_office_zip,
      // Optional fields
      formData.website,
      formData.company_description,
      formData.number_of_employees,
      formData.latitude,
      formData.longitude,
      formData.time_zone,
      formData.google_embedded_map,
      // Social profiles
      formData.facebook_url,
      formData.instagram_url,
      formData.twitter_url,
      formData.linkedin_url,
      formData.youtube_url,
      formData.pinterest_url,
      formData.google_business_profile_url,
      formData.yelp_url,
      formData.bbb_url,
      formData.tiktok_url,
      formData.dnb_url,
      formData.indeed_url,
      // Authorship
      formData.authorship_first_name,
      formData.authorship_last_name,
      // Logos - count if at least one is uploaded
      formData.logo_wide_full_color || formData.logo_square_full_color,
      // Branding
      brandingData.overview,
      brandingData.brand_color_1,
      brandingData.brand_color_2,
      brandingData.brand_typeface_1,
      brandingData.brand_typeface_2,
      // Top pages (count if any exist)
      topPagesData && topPagesData.length > 0 ? 'has_pages' : null,
    ];

    const filledFields = fields.filter(field => field !== null && field !== undefined && field !== '').length;
    const totalFields = fields.length;
    return Math.round((filledFields / totalFields) * 100);
  }, [formData, brandingData, topPagesData]);

  const handleInputChange = (field: keyof OrganizationForm, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSuccessMessage('');
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  // Google's geometry hands back a raw double (-122.08424989999999), but the
  // organization stores coordinates as decimal(_,7) — DRF rejects anything
  // longer than seven decimal places outright.
  const toStoredCoordinate = (value: number | undefined): number | undefined =>
    value === undefined || !Number.isFinite(value) ? undefined : Number(value.toFixed(7));

  // Google Places already knows the rest of the address and its coordinates,
  // so picking a suggestion fills city/state/zip and the geocode instead of
  // asking for six values that all describe the same place. Org coordinates
  // anchor the local-SEO ranking grid, which is why they were worth capturing.
  const handleAddressSelect = (parsed: ParsedAddress) => {
    setFormData(prev => ({
      ...prev,
      main_office_address_1: parsed.address_1 || prev.main_office_address_1,
      main_office_city: parsed.city || prev.main_office_city,
      main_office_state: parsed.state || prev.main_office_state,
      main_office_zip: parsed.zip_code || prev.main_office_zip,
      latitude: toStoredCoordinate(parsed.lat) ?? prev.latitude,
      longitude: toStoredCoordinate(parsed.lng) ?? prev.longitude,
    }));
  };

  // The author is stored as a loose name pair, so the picker matches on it.
  // No match means the author is someone off the roster, which is allowed.
  // An empty pair matches nobody — several rosters carry a half-set-up user
  // whose own name fields are blank.
  const authorName = `${formData.authorship_first_name || ''}${formData.authorship_last_name || ''}`;
  const selectedAuthorId =
    (authorName &&
      organizationUsers.find(
        (user) =>
          user.first_name === formData.authorship_first_name &&
          user.last_name === formData.authorship_last_name,
      )?.id) ||
    null;

  const handleAuthorSelect = (userId: number | null) => {
    const user = organizationUsers.find((u) => u.id === userId);
    if (!user) return;
    setFormData(prev => ({
      ...prev,
      authorship_first_name: user.first_name,
      authorship_last_name: user.last_name,
    }));
  };

  const handleBrandingChange = (field: keyof OrganizationOverview, value: any) => {
    // Handle color values - strip # prefix for backend storage
    if (field.startsWith('brand_color_') && value && value.startsWith('#')) {
      value = value.substring(1);
    }
    setBrandingData(prev => ({ ...prev, [field]: value }));
    setSuccessMessage('');
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  // Track which logo field is currently uploading
  const [uploadingLogoField, setUploadingLogoField] = useState<string | null>(null);

  const handleLogoUpload = async (file: File, fieldName: string) => {
    setUploadingLogoField(fieldName);
    setLogoUploadError(null);

    try {
      const response = await api.uploadOrganizationLogo(file, fieldName);

      // Invalidate the organization query to refetch with the new logo
      queryClient.invalidateQueries({ queryKey: ['organization'] });

      setSuccessMessage('Logo uploaded successfully!');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error: any) {
      console.error('Logo upload error:', error);
      const errorMessage = error?.responseData?.detail || error?.responseData?.[fieldName]?.[0] || 'Failed to upload logo';
      setLogoUploadError(errorMessage);
      setTimeout(() => setLogoUploadError(null), 5000);
    } finally {
      setUploadingLogoField(null);
    }
  };

  const addTopPage = () => {
    setEditingTopPage(null);
    setTopPageModalOpen(true);
  };

  const handleTopPageSave = (data: Omit<TopPage, 'id'>) => {
    if (editingTopPage) {
      // Update existing page
      updateTopPageMutation.mutate({ ...data, id: editingTopPage.id }, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['top-pages'] });
          setTopPageModalOpen(false);
          setEditingTopPage(null);
          setSuccessMessage('Top page updated successfully!');
          setTimeout(() => setSuccessMessage(''), 5000);
        },
      });
    } else {
      // Create new page
      createTopPageMutation.mutate(data, {
        onSuccess: (newPage) => {
          // Invalidate cache to refetch from server
          queryClient.invalidateQueries({ queryKey: ['top-pages'] });
          setTopPageModalOpen(false);
          setSuccessMessage('Top page added successfully!');
          setTimeout(() => setSuccessMessage(''), 5000);
        },
      });
    }
  };

  const removeTopPage = (id: number) => {
    if (window.confirm('Are you sure you want to delete this top page?')) {
      deleteTopPageMutation.mutate(id, {
        onSuccess: () => {
          // Invalidate cache to refetch from server
          queryClient.invalidateQueries({ queryKey: ['top-pages'] });
          setSuccessMessage('Top page deleted successfully!');
          setTimeout(() => setSuccessMessage(''), 5000);
        },
      });
    }
  };

  const addServiceArea = () => {
    setEditingServiceArea(null);
    setServiceAreaModalOpen(true);
  };

  const handleServiceAreaSave = (data: Omit<AreaServed, 'id'>) => {
    if (editingServiceArea) {
      // Update existing service area
      updateServiceAreaMutation.mutate({ ...data, id: editingServiceArea.id }, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['areas-served'] });
          setServiceAreaModalOpen(false);
          setEditingServiceArea(null);
          setSuccessMessage('Service area updated successfully!');
          setTimeout(() => setSuccessMessage(''), 5000);
        },
      });
    } else {
      // Create new service area
      createServiceAreaMutation.mutate(data, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['areas-served'] });
          setServiceAreaModalOpen(false);
          setSuccessMessage('Service area added successfully!');
          setTimeout(() => setSuccessMessage(''), 5000);
        },
      });
    }
  };

  const removeServiceArea = (id: number) => {
    if (window.confirm('Are you sure you want to delete this service area?')) {
      deleteServiceAreaMutation.mutate(id, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['areas-served'] });
          setSuccessMessage('Service area deleted successfully!');
          setTimeout(() => setSuccessMessage(''), 5000);
        },
      });
    }
  };


  const showSaveErrors = (next: Record<string, string>) => {
    setErrors(next);
    setSuccessMessage('');
    requestAnimationFrame(() => {
      errorSummary.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      errorSummary.current?.focus();
    });
  };

  const handleSave = async () => {
    if (updateOrgMutation.isPending || updateBrandingMutation.isPending) return;
    const organizationChanges = changedFields({ ...formData, manufacturers: selectedManufacturers, services: selectedServices }, orgBaseline.current, organizationFieldLabels);
    const brandingChanges = changedFields(brandingData, brandingBaseline.current, brandingFieldLabels);
    const validation = requiredFieldErrors(organizationChanges);
    if (Object.keys(brandingChanges).length && (!overviewData?.id || 'brand_color_1' in brandingChanges) && !brandingData.brand_color_1) {
      validation.brand_color_1 = 'Choose a Primary Color before saving branding.';
    }
    if (Object.keys(validation).length) { showSaveErrors(validation); return; }
    setErrors({});
    setSuccessMessage('');
    let companySaved = false;
    try {
      if (Object.keys(organizationChanges).length) {
        const updated = await updateOrgMutation.mutateAsync(organizationChanges);
        // Advance only the submitted fields so edits made during a request stay dirty.
        orgBaseline.current = { ...orgBaseline.current, ...organizationChanges };
        queryClient.setQueryData(['organization'], updated);
        companySaved = true;
      }
    } catch (error) { showSaveErrors(organizationSaveErrors(error)); return; }
    try {
      if (Object.keys(brandingChanges).length) {
        const updated = await updateBrandingMutation.mutateAsync(overviewData?.id ? brandingChanges : { ...brandingChanges, brand_color_1: brandingData.brand_color_1 });
        brandingBaseline.current = { ...brandingBaseline.current, ...brandingChanges };
        queryClient.setQueryData(['organization-overview'], updated);
      }
    } catch (error) {
      showSaveErrors({ ...organizationSaveErrors(error), save_status: companySaved ? 'Company information was saved. Branding was not saved; correct the errors below and save again.' : 'Branding was not saved.' });
      return;
    }
    setSuccessMessage(companySaved || Object.keys(brandingChanges).length ? 'All changes saved successfully.' : 'No changes to save.');
  };

  if (orgLoading || overviewLoading || topPagesLoading || serviceAreasLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (orgError) return <Alert color="danger" endDecorator={<Button onClick={() => refetchOrg()} variant="soft" color="danger">Retry</Button>}>Could not load company details.</Alert>;

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
<PageHeader title="Company details" description="Keep your business information, service areas, and brand assets up to date." actions={<Button
          variant="solid"
          color="primary"
          startDecorator={<SaveIcon />}
          onClick={handleSave}
          loading={updateOrgMutation.isPending || updateBrandingMutation.isPending}
        >
          Save All Changes
        </Button>} />

      {successMessage && (
        <Alert color="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      {Object.values(errors).some(Boolean) && (
        <Alert color="danger" role="alert" ref={errorSummary} tabIndex={-1} sx={{ mb: 3, alignItems: 'flex-start' }}>
          <Box>
            <Typography fontWeight="lg">Please review these fields before saving</Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {Object.entries(errors).filter(([, message]) => message).map(([field, message]) => (
                <li key={field}>{['general', 'save_status'].includes(field) ? message : <>{(organizationFieldLabels[field] || brandingFieldLabels[field]) ? <a href={field === 'manufacturers' ? '#company-brands' : field === 'services' ? '#company-services' : `#company-field-${field}`}>{fieldLabel(field)}</a> : fieldLabel(field)}: {message}</>}</li>
              ))}
            </Box>
          </Box>
        </Alert>
      )}

<Box component="nav" aria-label="Company details sections" sx={{ display: 'flex', flexWrap: 'wrap', gap: .5, mb: 3 }}><Button component="a" href="#company-business" key="business" size="sm" variant="plain" color="neutral">Business Information</Button><Button component="a" href="#company-address" key="address" size="sm" variant="plain" color="neutral">Address & Location</Button><Button component="a" href="#company-service-areas" key="service-areas" size="sm" variant="plain" color="neutral">Service Areas</Button><Button component="a" href="#company-brands" key="brands" size="sm" variant="plain" color="neutral">Manufacturers & Brands</Button><Button component="a" href="#company-services" key="services" size="sm" variant="plain" color="neutral">Services Offered</Button><Button component="a" href="#company-social" key="social" size="sm" variant="plain" color="neutral">Social Profiles</Button><Button component="a" href="#company-authorship" key="authorship" size="sm" variant="plain" color="neutral">Authorship</Button><Button component="a" href="#company-pages" key="pages" size="sm" variant="plain" color="neutral">Top Pages</Button><Button component="a" href="#company-branding" key="branding" size="sm" variant="plain" color="neutral">Branding</Button></Box>
      {/* Knowledge Graph Progress */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography level="h3" sx={{ mb: 2 }}>Company profile completeness</Typography>
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography level="body-md">Profile Completeness</Typography>
              <Typography level="body-md">{knowledgeGraphScore}%</Typography>
            </Stack>
            <LinearProgress
              determinate
              value={knowledgeGraphScore}
              color={knowledgeGraphScore >= 80 ? 'success' : knowledgeGraphScore >= 50 ? 'warning' : 'danger'}
              sx={{
                height: 10,
                borderRadius: 5,
                '--LinearProgress-thickness': '10px',
                '--LinearProgress-progressThickness': '10px',
              }}
            />
          </Box>
          <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
            Profile completeness is a recommendation, not a requirement to save. Only changed fields are saved. Fields marked * cannot be cleared when edited; other fields are optional.
          </Typography>
        </CardContent>
      </Card>

      {/* Business Information */}
      <Card id="company-business" sx={{ mb: 3, scrollMarginTop: '112px' }}>
        <CardContent>
          <Typography level="h3" sx={{ mb: 3 }}>Business Information</Typography>
          <Grid container spacing={2}>
            <Grid xs={12} md={6}>
              <FormControl id="company-field-name" error={Boolean(errors.name)} required={requiredOrganizationFields.has('name')}>
                <FormLabel>Company Name</FormLabel>
                <Input
                  slotProps={{ input: { 'aria-invalid': Boolean(errors.name) } }}
                  value={formData.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                />
                {(errors.name) && <FormHelperText>{errors.name}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={12} md={6}>
              <FormControl id="company-field-website" error={Boolean(errors.website)} required={requiredOrganizationFields.has('website')}>
                <FormLabel>Website</FormLabel>
                <Input
                  slotProps={{ input: { 'aria-invalid': Boolean(errors.website) } }}
                  type="url"
                  value={formData.website || ''}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  placeholder="https://example.com"
                />
                {(errors.website) && <FormHelperText>{errors.website}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={12} md={6}>
              <FormControl id="company-field-payment_url" error={Boolean(errors.payment_url)} required={requiredOrganizationFields.has('payment_url')}>
                <FormLabel>Pay Online URL</FormLabel>
                <Input
                  slotProps={{ input: { 'aria-invalid': Boolean(errors.payment_url) } }}
                  type="url"
                  value={formData.payment_url || ''}
                  onChange={(e) => handleInputChange('payment_url', e.target.value)}
                  placeholder="https://pay.example.com"
                />
                <FormHelperText>
                  Where customers can pay an invoice online — a Stripe payment link, QuickBooks portal,
                  or similar. Included on invoice views and default invoice emails, and on PDFs as a clickable payment link and scannable QR code.
                  Leave blank to hide both. Also editable under Operations → Billing → Settings.
                </FormHelperText>
                {(errors.payment_url) && <FormHelperText>{errors.payment_url}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={12} md={6}>
              <FormControl id="company-field-company_email" error={Boolean(errors.company_email)} required={requiredOrganizationFields.has('company_email')}>
                <FormLabel>Company Email</FormLabel>
                <Input
                  slotProps={{ input: { 'aria-invalid': Boolean(errors.company_email) } }}
                  type="email"
                  value={formData.company_email || ''}
                  onChange={(e) => handleInputChange('company_email', e.target.value)}
                />
                {(errors.company_email) && <FormHelperText>{errors.company_email}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={12} md={6}>
              <FormControl id="company-field-company_phone_number" error={Boolean(errors.company_phone_number)} required={requiredOrganizationFields.has('company_phone_number')}>
                <FormLabel>Company Phone</FormLabel>
                <Input
                  slotProps={{ input: { 'aria-invalid': Boolean(errors.company_phone_number) } }}
                  type="tel"
                  value={formData.company_phone_number || ''}
                  onChange={(e) => handleInputChange('company_phone_number', e.target.value)}
                />
                {(errors.company_phone_number) && <FormHelperText>{errors.company_phone_number}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={12}>
              <FormControl id="company-field-company_description" error={Boolean(errors.company_description)} required={requiredOrganizationFields.has('company_description')}>
                <FormLabel>Company Description</FormLabel>
                <Textarea
                  slotProps={{ textarea: { 'aria-invalid': Boolean(errors.company_description) } }}
                  minRows={3}
                  value={formData.company_description || ''}
                  onChange={(e) => handleInputChange('company_description', e.target.value)}
                  placeholder="Describe your business..."
                />
                {(errors.company_description) && <FormHelperText>{errors.company_description}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={12}>
              <FormControl id="company-field-default_email_signature" error={Boolean(errors.default_email_signature)} required={requiredOrganizationFields.has('default_email_signature')}>
                <FormLabel>Default Email Signature</FormLabel>
                <Textarea
                  slotProps={{ textarea: { 'aria-invalid': Boolean(errors.default_email_signature) } }}
                  minRows={3}
                  value={formData.default_email_signature || ''}
                  onChange={(e) => handleInputChange('default_email_signature', e.target.value)}
                  placeholder="Company name, phone, website"
                />
                <FormHelperText>
                  Signs mail from anyone who has not saved a signature of their own under
                  My Profile. Started from your company details — edit or clear it.
                </FormHelperText>
                {(errors.default_email_signature) && <FormHelperText>{errors.default_email_signature}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={12} md={6}>
              <FormControl id="company-field-number_of_employees" error={Boolean(errors.number_of_employees)} required={requiredOrganizationFields.has('number_of_employees')}>
                <FormLabel>Number of Employees</FormLabel>
                <Input
                  slotProps={{ input: { 'aria-invalid': Boolean(errors.number_of_employees) } }}
                  type="number"
                  value={formData.number_of_employees ?? ''}
                  onChange={(e) => handleInputChange('number_of_employees', e.target.value === '' ? null : Number(e.target.value))}
                />
                {(errors.number_of_employees) && <FormHelperText>{errors.number_of_employees}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={12} md={6}>
              <FormControl id="company-field-years_in_business" error={Boolean(errors.years_in_business)} required={requiredOrganizationFields.has('years_in_business')}>
                <FormLabel>Years in Business</FormLabel>
                <Input
                  slotProps={{ input: { 'aria-invalid': Boolean(errors.years_in_business) } }}
                  type="number"
                  value={formData.years_in_business ?? ''}
                  onChange={(e) => handleInputChange('years_in_business', e.target.value === '' ? null : Number(e.target.value))}
                />
                {(errors.years_in_business) && <FormHelperText>{errors.years_in_business}</FormHelperText>}
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Address & Map */}
      <Card id="company-address" sx={{ mb: 3, scrollMarginTop: '112px' }}>
        <CardContent>
          <Typography level="h3" sx={{ mb: 3 }}>Address & Location</Typography>
          <Grid container spacing={2}>
            <Grid xs={12}>
              <FormControl id="company-field-main_office_address_1" error={Boolean(errors.main_office_address_1)} required={requiredOrganizationFields.has('main_office_address_1')}>
                <FormLabel>Address Line 1</FormLabel>
                <AddressAutocompleteInput
                  value={formData.main_office_address_1 || ''}
                  onChange={(value) => handleInputChange('main_office_address_1', value)}
                  onAddressSelect={handleAddressSelect}
                  placeholder="Start typing your office address"
                />
                <FormHelperText>
                  Picking a suggestion fills in the city, state, ZIP and coordinates.
                </FormHelperText>
                {(errors.main_office_address_1) && <FormHelperText>{errors.main_office_address_1}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={12}>
              <FormControl id="company-field-main_office_address_2" error={Boolean(errors.main_office_address_2)} required={requiredOrganizationFields.has('main_office_address_2')}>
                <FormLabel>Address Line 2</FormLabel>
                <Input
                  slotProps={{ input: { 'aria-invalid': Boolean(errors.main_office_address_2) } }}
                  value={formData.main_office_address_2 || ''}
                  onChange={(e) => handleInputChange('main_office_address_2', e.target.value)}
                />
                {(errors.main_office_address_2) && <FormHelperText>{errors.main_office_address_2}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={12} md={4}>
              <FormControl id="company-field-main_office_city" error={Boolean(errors.main_office_city)} required={requiredOrganizationFields.has('main_office_city')}>
                <FormLabel>City</FormLabel>
                <Input
                  slotProps={{ input: { 'aria-invalid': Boolean(errors.main_office_city) } }}
                  value={formData.main_office_city || ''}
                  onChange={(e) => handleInputChange('main_office_city', e.target.value)}
                />
                {(errors.main_office_city) && <FormHelperText>{errors.main_office_city}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={12} md={4}>
              <FormControl id="company-field-main_office_state" error={Boolean(errors.main_office_state)} required={requiredOrganizationFields.has('main_office_state')}>
                <FormLabel>State</FormLabel>
                <Input
                  slotProps={{ input: { 'aria-invalid': Boolean(errors.main_office_state) } }}
                  value={formData.main_office_state || ''}
                  onChange={(e) => handleInputChange('main_office_state', e.target.value)}
                />
                {(errors.main_office_state) && <FormHelperText>{errors.main_office_state}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={12} md={4}>
              <FormControl id="company-field-main_office_zip" error={Boolean(errors.main_office_zip)} required={requiredOrganizationFields.has('main_office_zip')}>
                <FormLabel>Zip Code</FormLabel>
                <Input
                  slotProps={{ input: { 'aria-invalid': Boolean(errors.main_office_zip) } }}
                  value={formData.main_office_zip || ''}
                  onChange={(e) => handleInputChange('main_office_zip', e.target.value)}
                />
                {(errors.main_office_zip) && <FormHelperText>{errors.main_office_zip}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={12} md={6}>
              {/* Derived from the address above rather than typed: these
                  anchor the local-SEO ranking grid and the map, and a
                  hand-entered pair is the one people get wrong. */}
              <FormControl id="company-field-latitude" error={Boolean(errors.latitude || errors.longitude)} required={requiredOrganizationFields.has('latitude')}>
                <FormLabel>Coordinates</FormLabel>
                {editCoordinates ? (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Input
                  slotProps={{ input: { 'aria-invalid': Boolean(errors.latitude) } }}
                      type="number"
                      value={formData.latitude ?? ''}
                      onChange={(e) => handleInputChange('latitude', toStoredCoordinate(parseFloat(e.target.value)))}
                      placeholder="Latitude, e.g. 40.7128"
                    />
                    <Input
                  slotProps={{ input: { 'aria-invalid': Boolean(errors.latitude) } }}
                      type="number"
                      value={formData.longitude ?? ''}
                      onChange={(e) => handleInputChange('longitude', toStoredCoordinate(parseFloat(e.target.value)))}
                      placeholder="Longitude, e.g. -74.0060"
                    />
                  </Stack>
                ) : (
                  <Input
                  slotProps={{ input: { 'aria-invalid': Boolean(errors.latitude) } }}
                    readOnly
                    value={
                      formData.latitude != null && formData.longitude != null
                        ? `${formData.latitude}, ${formData.longitude}`
                        : ''
                    }
                    placeholder="Set when you pick an address above"
                  />
                )}
                <FormHelperText>Used by the local ranking grid and map features.</FormHelperText>
                <Button
                  variant="plain"
                  size="sm"
                  sx={{ alignSelf: 'flex-start', minHeight: 0, px: 0.5 }}
                  onClick={() => setEditCoordinates(!editCoordinates)}
                >
                  {editCoordinates ? 'Done' : 'Enter manually'}
                </Button>
                {(errors.latitude || errors.longitude) && <FormHelperText>{errors.latitude || errors.longitude}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={12} md={6}>
              <FormControl id="company-field-time_zone" error={Boolean(errors.time_zone)} required={requiredOrganizationFields.has('time_zone')}>
                <FormLabel>Time Zone</FormLabel>
                <Select
                  value={formData.time_zone || ''}
                  onChange={(_, value) => handleInputChange('time_zone', value)}
                  placeholder="Select time zone..."
                >
                  <Option value="America/New_York">Eastern Time (ET) - US</Option>
                  <Option value="America/Toronto">Eastern Time (ET) - Canada</Option>
                  <Option value="America/Halifax">Atlantic Time (AT) - Canada</Option>
                  <Option value="America/St_Johns">Newfoundland Time (NT) - Canada</Option>
                  <Option value="America/Chicago">Central Time (CT) - US</Option>
                  <Option value="America/Winnipeg">Central Time (CT) - Canada</Option>
                  <Option value="America/Mexico_City">Central Time (CT) - Mexico</Option>
                  <Option value="America/Denver">Mountain Time (MT) - US</Option>
                  <Option value="America/Edmonton">Mountain Time (MT) - Canada</Option>
                  <Option value="America/Hermosillo">Mountain Time (MT) - Mexico</Option>
                  <Option value="America/Los_Angeles">Pacific Time (PT) - US</Option>
                  <Option value="America/Vancouver">Pacific Time (PT) - Canada</Option>
                  <Option value="America/Tijuana">Pacific Time (PT) - Mexico</Option>
                  <Option value="America/Anchorage">Alaska Time (AKT) - US</Option>
                  <Option value="America/Adak">Hawaii-Aleutian Time (HAT) - US</Option>
                </Select>
                <FormHelperText>Scheduling uses this company time zone for calendar times and task dates, even when a team member’s device uses a different zone. Daylight saving changes apply automatically where observed.</FormHelperText>
                {(errors.time_zone) && <FormHelperText>{errors.time_zone}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={12}>
              <FormControl id="company-field-google_embedded_map" error={Boolean(errors.google_embedded_map)} required={requiredOrganizationFields.has('google_embedded_map')}>
                <FormLabel>Google Maps Embed Code</FormLabel>
                <Textarea
                  slotProps={{ textarea: { 'aria-invalid': Boolean(errors.google_embedded_map) } }}
                  minRows={3}
                  value={formData.google_embedded_map || ''}
                  onChange={(e) => handleInputChange('google_embedded_map', e.target.value)}
                  placeholder='<iframe src="https://www.google.com/maps/embed?..." ...'
                />
                {(errors.google_embedded_map) && <FormHelperText>{errors.google_embedded_map}</FormHelperText>}
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Service Areas */}
      <Card id="company-service-areas" sx={{ mb: 3, scrollMarginTop: '112px' }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1} sx={{ mb: 3 }}>
            <Typography level="h3" sx={{ fontSize: { xs: '1.25rem', md: undefined } }}>Service Areas</Typography>
            <Button
              variant="outlined"
              color="primary"
              size="sm"
              startDecorator={<AddIcon />}
              onClick={addServiceArea}
            >
              Add Area
            </Button>
          </Stack>
          
          <Stack spacing={2}>
            {serviceAreasData && Array.isArray(serviceAreasData) && serviceAreasData.map((area) => (
              <Box key={area.id} sx={{ p: 2, bgcolor: 'background.level1', borderRadius: 'md' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid xs={12} md={5}>
                    <FormControl size="sm">
                      <FormLabel>City</FormLabel>
                      <Input
                        value={area.city || ''}
                        readOnly
                      />
                    </FormControl>
                  </Grid>
                  <Grid xs={12} md={5}>
                    <FormControl size="sm">
                      <FormLabel>State</FormLabel>
                      <Input
                        value={area.state || ''}
                        readOnly
                      />
                    </FormControl>
                  </Grid>
                  <Grid xs={12} md={2} sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                    <IconButton
                      color="primary"
                      variant="plain"
                      size="sm"
                      onClick={() => {
                        setEditingServiceArea(area);
                        setServiceAreaModalOpen(true);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="danger"
                      variant="plain"
                      size="sm"
                      onClick={() => removeServiceArea(area.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>
            ))}
            
            {(!serviceAreasData || serviceAreasData.length === 0) && (
              <Typography level="body-sm" sx={{ textAlign: 'center', color: 'text.tertiary', py: 2 }}>
                No service areas added yet. Click "Add Area" to define where you provide services.
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Manufacturers & Brands */}
      <Card id="company-brands" sx={{ mb: 3, scrollMarginTop: '112px' }}>
        <CardContent>
          <Typography level="h3" sx={{ mb: 1 }}>Manufacturers & Brands</Typography>
          <Typography level="body-sm" sx={{ mb: 3, color: 'neutral.600' }}>
            Select the manufacturers you work with. This helps with content generation and SEO.
          </Typography>
          <ManufacturerSelector
            selectedIds={selectedManufacturers}
            onChange={setSelectedManufacturers}
          />
        </CardContent>
      </Card>

      {/* Services Offered */}
      <Card id="company-services" sx={{ mb: 3, scrollMarginTop: '112px' }}>
        <CardContent>
          <Typography level="h3" sx={{ mb: 1 }}>Services Offered</Typography>
          <Typography level="body-sm" sx={{ mb: 3, color: 'neutral.600' }}>
            Select the services your company offers. This information is used for SEO and marketing content generation.
          </Typography>

          {Object.entries(PREDEFINED_SERVICES).map(([category, services]) => (
            <Box key={category} sx={{ mb: 3 }}>
              <Typography level="title-md" sx={{ mb: 1.5 }}>{category}</Typography>
              <Grid container spacing={1}>
                {services.map((service) => (
                  <Grid xs={12} sm={6} md={4} key={service}>
                    <Checkbox
                      label={service}
                      checked={selectedServices.includes(service)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedServices((prev) => [...prev, service]);
                        } else {
                          setSelectedServices((prev) => prev.filter((s) => s !== service));
                        }
                      }}
                      size="sm"
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}

          <Divider sx={{ my: 2 }} />

          <Typography level="title-md" sx={{ mb: 1.5 }}>Custom Services</Typography>
          <Typography level="body-sm" sx={{ mb: 2, color: 'neutral.600' }}>
            Add any additional services not listed above (e.g., Fencing, Siding, Windows, Gutters).
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Input
              placeholder="Enter a custom service..."
              value={customServiceInput}
              onChange={(e) => setCustomServiceInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && customServiceInput.trim()) {
                  e.preventDefault();
                  const trimmed = customServiceInput.trim();
                  if (!selectedServices.includes(trimmed)) {
                    setSelectedServices((prev) => [...prev, trimmed]);
                  }
                  setCustomServiceInput('');
                }
              }}
              sx={{ flex: 1 }}
            />
            <Button
              variant="outlined"
              color="primary"
              startDecorator={<AddIcon />}
              disabled={!customServiceInput.trim()}
              onClick={() => {
                const trimmed = customServiceInput.trim();
                if (trimmed && !selectedServices.includes(trimmed)) {
                  setSelectedServices((prev) => [...prev, trimmed]);
                }
                setCustomServiceInput('');
              }}
            >
              Add
            </Button>
          </Stack>
          {customServices.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {customServices.map((service) => (
                <Chip
                  key={service}
                  variant="soft"
                  color="primary"
                  endDecorator={
                    <ChipDelete
                      onDelete={() =>
                        setSelectedServices((prev) => prev.filter((s) => s !== service))
                      }
                    />
                  }
                >
                  {service}
                </Chip>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Social Profiles */}
      <Card id="company-social" sx={{ mb: 3, scrollMarginTop: '112px' }}>
        <CardContent>
          <Typography level="h3" sx={{ mb: 3 }}>Social Profiles</Typography>
          <Grid container spacing={2}>
            <Grid xs={12} md={6}>
              <FormControl id="company-field-facebook_url" error={Boolean(errors.facebook_url)} required={requiredOrganizationFields.has('facebook_url')}>
                <FormLabel>Facebook</FormLabel>
                <Input
                  slotProps={{ input: { 'aria-invalid': Boolean(errors.facebook_url) } }}
                  type="url"
                  value={formData.facebook_url || ''}
                  onChange={(e) => handleInputChange('facebook_url', e.target.value)}
                  placeholder="https://facebook.com/yourpage"
                />
                {(errors.facebook_url) && <FormHelperText>{errors.facebook_url}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={12} md={6}>
              <FormControl id="company-field-instagram_url" error={Boolean(errors.instagram_url)} required={requiredOrganizationFields.has('instagram_url')}>
                <FormLabel>Instagram</FormLabel>
                <Input
                  slotProps={{ input: { 'aria-invalid': Boolean(errors.instagram_url) } }}
                  type="url"
                  value={formData.instagram_url || ''}
                  onChange={(e) => handleInputChange('instagram_url', e.target.value)}
                  placeholder="https://instagram.com/youraccount"
                />
                {(errors.instagram_url) && <FormHelperText>{errors.instagram_url}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={12} md={6}>
              <FormControl id="company-field-twitter_url" error={Boolean(errors.twitter_url)} required={requiredOrganizationFields.has('twitter_url')}>
                <FormLabel>Twitter</FormLabel>
                <Input
                  slotProps={{ input: { 'aria-invalid': Boolean(errors.twitter_url) } }}
                  type="url"
                  value={formData.twitter_url || ''}
                  onChange={(e) => handleInputChange('twitter_url', e.target.value)}
                  placeholder="https://twitter.com/youraccount"
                />
                {(errors.twitter_url) && <FormHelperText>{errors.twitter_url}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={12} md={6}>
              <FormControl id="company-field-linkedin_url" error={Boolean(errors.linkedin_url)} required={requiredOrganizationFields.has('linkedin_url')}>
                <FormLabel>LinkedIn</FormLabel>
                <Input
                  slotProps={{ input: { 'aria-invalid': Boolean(errors.linkedin_url) } }}
                  type="url"
                  value={formData.linkedin_url || ''}
                  onChange={(e) => handleInputChange('linkedin_url', e.target.value)}
                  placeholder="https://linkedin.com/company/yourcompany"
                />
                {(errors.linkedin_url) && <FormHelperText>{errors.linkedin_url}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={12} md={6}>
              <FormControl id="company-field-youtube_url" error={Boolean(errors.youtube_url)} required={requiredOrganizationFields.has('youtube_url')}>
                <FormLabel>YouTube</FormLabel>
                <Input
                  slotProps={{ input: { 'aria-invalid': Boolean(errors.youtube_url) } }}
                  type="url"
                  value={formData.youtube_url || ''}
                  onChange={(e) => handleInputChange('youtube_url', e.target.value)}
                  placeholder="https://youtube.com/yourchannel"
                />
                {(errors.youtube_url) && <FormHelperText>{errors.youtube_url}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={12} md={6}>
              <FormControl id="company-field-pinterest_url" error={Boolean(errors.pinterest_url)} required={requiredOrganizationFields.has('pinterest_url')}>
                <FormLabel>Pinterest</FormLabel>
                <Input
                  slotProps={{ input: { 'aria-invalid': Boolean(errors.pinterest_url) } }}
                  type="url"
                  value={formData.pinterest_url || ''}
                  onChange={(e) => handleInputChange('pinterest_url', e.target.value)}
                  placeholder="https://pinterest.com/youraccount"
                />
                {(errors.pinterest_url) && <FormHelperText>{errors.pinterest_url}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={12} md={6}>
              <FormControl id="company-field-google_business_profile_url" error={Boolean(errors.google_business_profile_url)} required={requiredOrganizationFields.has('google_business_profile_url')}>
                <FormLabel>Google Business Profile</FormLabel>
                <Input
                  slotProps={{ input: { 'aria-invalid': Boolean(errors.google_business_profile_url) } }}
                  type="url"
                  value={formData.google_business_profile_url || ''}
                  onChange={(e) => handleInputChange('google_business_profile_url', e.target.value)}
                  placeholder="https://g.page/yourbusiness"
                />
                {(errors.google_business_profile_url) && <FormHelperText>{errors.google_business_profile_url}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={12} md={6}>
              <FormControl id="company-field-yelp_url" error={Boolean(errors.yelp_url)} required={requiredOrganizationFields.has('yelp_url')}>
                <FormLabel>Yelp</FormLabel>
                <Input
                  slotProps={{ input: { 'aria-invalid': Boolean(errors.yelp_url) } }}
                  type="url"
                  value={formData.yelp_url || ''}
                  onChange={(e) => handleInputChange('yelp_url', e.target.value)}
                  placeholder="https://yelp.com/biz/yourbusiness"
                />
                {(errors.yelp_url) && <FormHelperText>{errors.yelp_url}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={12} md={6}>
              <FormControl id="company-field-bbb_url" error={Boolean(errors.bbb_url)} required={requiredOrganizationFields.has('bbb_url')}>
                <FormLabel>Better Business Bureau (BBB)</FormLabel>
                <Input
                  slotProps={{ input: { 'aria-invalid': Boolean(errors.bbb_url) } }}
                  type="url"
                  value={formData.bbb_url || ''}
                  onChange={(e) => handleInputChange('bbb_url', e.target.value)}
                  placeholder="https://www.bbb.org/yourbusiness"
                />
                {(errors.bbb_url) && <FormHelperText>{errors.bbb_url}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={12} md={6}>
              <FormControl id="company-field-tiktok_url" error={Boolean(errors.tiktok_url)} required={requiredOrganizationFields.has('tiktok_url')}>
                <FormLabel>TikTok</FormLabel>
                <Input
                  slotProps={{ input: { 'aria-invalid': Boolean(errors.tiktok_url) } }}
                  type="url"
                  value={formData.tiktok_url || ''}
                  onChange={(e) => handleInputChange('tiktok_url', e.target.value)}
                  placeholder="https://tiktok.com/@youraccount"
                />
                {(errors.tiktok_url) && <FormHelperText>{errors.tiktok_url}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={12} md={6}>
              <FormControl id="company-field-dnb_url" error={Boolean(errors.dnb_url)} required={requiredOrganizationFields.has('dnb_url')}>
                <FormLabel>Dun & Bradstreet</FormLabel>
                <Input
                  slotProps={{ input: { 'aria-invalid': Boolean(errors.dnb_url) } }}
                  type="url"
                  value={formData.dnb_url || ''}
                  onChange={(e) => handleInputChange('dnb_url', e.target.value)}
                  placeholder="https://www.dnb.com/yourbusiness"
                />
                {(errors.dnb_url) && <FormHelperText>{errors.dnb_url}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={12} md={6}>
              <FormControl id="company-field-indeed_url" error={Boolean(errors.indeed_url)} required={requiredOrganizationFields.has('indeed_url')}>
                <FormLabel>Indeed</FormLabel>
                <Input
                  slotProps={{ input: { 'aria-invalid': Boolean(errors.indeed_url) } }}
                  type="url"
                  value={formData.indeed_url || ''}
                  onChange={(e) => handleInputChange('indeed_url', e.target.value)}
                  placeholder="https://indeed.com/cmp/yourcompany"
                />
                {(errors.indeed_url) && <FormHelperText>{errors.indeed_url}</FormHelperText>}
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Authorship */}
      <Card id="company-authorship" sx={{ mb: 3, scrollMarginTop: '112px' }}>
        <CardContent>
          <Typography level="h3" sx={{ mb: 3 }}>Authorship</Typography>
          <Grid container spacing={2}>
            <Grid xs={12}>
              <FormControl>
                <FormLabel>Author</FormLabel>
                <Select
                  value={selectedAuthorId}
                  onChange={(_, value) => handleAuthorSelect(value as number | null)}
                  placeholder="Choose a team member"
                >
                  {organizationUsers.map((user) => (
                    <Option key={user.id} value={user.id}>
                      {user.full_name || `${user.first_name} ${user.last_name}`.trim() || user.email}
                    </Option>
                  ))}
                </Select>
                <FormHelperText>
                  Bylines your published content. Picking a team member fills the name below;
                  edit it if the author is someone not on the roster.
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid xs={12} md={6}>
              <FormControl id="company-field-authorship_first_name" error={Boolean(errors.authorship_first_name)} required={requiredOrganizationFields.has('authorship_first_name')}>
                <FormLabel>Author First Name</FormLabel>
                <Input
                  slotProps={{ input: { 'aria-invalid': Boolean(errors.authorship_first_name) } }}
                  value={formData.authorship_first_name || ''}
                  onChange={(e) => handleInputChange('authorship_first_name', e.target.value)}
                  placeholder="Content author's first name"
                />
                {(errors.authorship_first_name) && <FormHelperText>{errors.authorship_first_name}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid xs={12} md={6}>
              <FormControl id="company-field-authorship_last_name" error={Boolean(errors.authorship_last_name)} required={requiredOrganizationFields.has('authorship_last_name')}>
                <FormLabel>Author Last Name</FormLabel>
                <Input
                  slotProps={{ input: { 'aria-invalid': Boolean(errors.authorship_last_name) } }}
                  value={formData.authorship_last_name || ''}
                  onChange={(e) => handleInputChange('authorship_last_name', e.target.value)}
                  placeholder="Content author's last name"
                />
                {(errors.authorship_last_name) && <FormHelperText>{errors.authorship_last_name}</FormHelperText>}
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Top Pages */}
      <Card id="company-pages" sx={{ mb: 3, scrollMarginTop: '112px' }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1} sx={{ mb: 3 }}>
            <Typography level="h3" sx={{ fontSize: { xs: '1.25rem', md: undefined } }}>Top Pages</Typography>
            <Button
              variant="outlined"
              color="primary"
              size="sm"
              startDecorator={<AddIcon />}
              onClick={addTopPage}
            >
              Add Page
            </Button>
          </Stack>
          
          <Stack spacing={2}>
            {topPagesData && Array.isArray(topPagesData) && topPagesData.map((page, index) => (
              <Box key={page.id} sx={{ p: 2, bgcolor: 'background.level1', borderRadius: 'md' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid xs={12} md={3}>
                    <FormControl size="sm">
                      <FormLabel>URL</FormLabel>
                      <Input
                        type="url"
                        value={page.url || ''}
                        readOnly
                        placeholder="https://example.com/page"
                      />
                    </FormControl>
                  </Grid>
                  <Grid xs={12} md={2.5}>
                    <FormControl size="sm">
                      <FormLabel>Keyword 1</FormLabel>
                      <Input
                        value={page.keyword_1 || ''}
                        readOnly
                        placeholder="Primary keyword"
                      />
                    </FormControl>
                  </Grid>
                  <Grid xs={12} md={2.5}>
                    <FormControl size="sm">
                      <FormLabel>Keyword 2</FormLabel>
                      <Input
                        value={page.keyword_2 || ''}
                        readOnly
                        placeholder="Secondary keyword"
                      />
                    </FormControl>
                  </Grid>
                  <Grid xs={12} md={2.5}>
                    <FormControl size="sm">
                      <FormLabel>Keyword 3</FormLabel>
                      <Input
                        value={page.keyword_3 || ''}
                        readOnly
                        placeholder="Tertiary keyword"
                      />
                    </FormControl>
                  </Grid>
                  <Grid xs={12} md={1.5} sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                    <IconButton
                      color="primary"
                      variant="plain"
                      size="sm"
                      onClick={() => {
                        setEditingTopPage(page);
                        setTopPageModalOpen(true);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="danger"
                      variant="plain"
                      size="sm"
                      onClick={() => removeTopPage(page.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>
            ))}
            
            {(!topPagesData || topPagesData.length === 0) && (
              <Typography level="body-sm" sx={{ textAlign: 'center', color: 'text.tertiary', py: 2 }}>
                No top pages added yet. Click "Add Page" to start tracking your important pages.
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Branding */}
      <Card id="company-branding" sx={{ mb: 3, scrollMarginTop: '112px' }}>
        <CardContent>
          <Typography level="h3" sx={{ mb: 3 }}>Branding</Typography>
          
          {/* Logo Upload Grid */}
          {logoUploadError && (
            <Alert color="danger" sx={{ mb: 2 }}>
              {logoUploadError}
            </Alert>
          )}

          {/* Square Logos */}
          <Typography level="title-md" sx={{ mb: 1 }}>Company Logos - Square</Typography>
          <Typography level="body-sm" sx={{ mb: 2, color: 'text.tertiary' }}>
            Square logos are ideal for favicons, social media profiles, and app icons.
          </Typography>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {/* Square Full-Color */}
            <Grid xs={12} md={4}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography level="title-sm" sx={{ mb: 1 }}>Full-Color</Typography>
                <Box sx={{
                  height: 80,
                  bgcolor: 'background.level1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 'sm',
                  mb: 1
                }}>
                  {formData.logo_square_full_color ? (
                    <Box component="img" src={formData.logo_square_full_color} alt="Square Full-Color" sx={{ maxHeight: 70, maxWidth: '90%', objectFit: 'contain' }} />
                  ) : (
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>No logo</Typography>
                  )}
                </Box>
                <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 1 }}>
                  For general use on light backgrounds
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  size="sm"
                  fullWidth
                  startDecorator={uploadingLogoField === 'logo_square_full_color' ? <CircularProgress size="sm" /> : <UploadIcon />}
                  component="label"
                  disabled={uploadingLogoField !== null}
                >
                  {uploadingLogoField === 'logo_square_full_color' ? 'Uploading...' : 'Upload'}
                  <input type="file" hidden accept="image/*" disabled={uploadingLogoField !== null}
                    onChange={(e) => { const file = e.target.files?.[0]; if (file) handleLogoUpload(file, 'logo_square_full_color'); }} />
                </Button>
              </Card>
            </Grid>
            {/* Square Light */}
            <Grid xs={12} md={4}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography level="title-sm" sx={{ mb: 1 }}>Light</Typography>
                <Box sx={{
                  height: 80,
                  bgcolor: 'neutral.800',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 'sm',
                  mb: 1
                }}>
                  {formData.logo_square_light ? (
                    <Box component="img" src={formData.logo_square_light} alt="Square Light" sx={{ maxHeight: 70, maxWidth: '90%', objectFit: 'contain' }} />
                  ) : (
                    <Typography level="body-xs" sx={{ color: 'neutral.400' }}>No logo</Typography>
                  )}
                </Box>
                <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 1 }}>
                  For dark backgrounds and app splash screens
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  size="sm"
                  fullWidth
                  startDecorator={uploadingLogoField === 'logo_square_light' ? <CircularProgress size="sm" /> : <UploadIcon />}
                  component="label"
                  disabled={uploadingLogoField !== null}
                >
                  {uploadingLogoField === 'logo_square_light' ? 'Uploading...' : 'Upload'}
                  <input type="file" hidden accept="image/*" disabled={uploadingLogoField !== null}
                    onChange={(e) => { const file = e.target.files?.[0]; if (file) handleLogoUpload(file, 'logo_square_light'); }} />
                </Button>
              </Card>
            </Grid>
            {/* Square Dark */}
            <Grid xs={12} md={4}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography level="title-sm" sx={{ mb: 1 }}>Dark</Typography>
                <Box sx={{
                  height: 80,
                  bgcolor: 'background.level1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 'sm',
                  mb: 1
                }}>
                  {formData.logo_square_dark ? (
                    <Box component="img" src={formData.logo_square_dark} alt="Square Dark" sx={{ maxHeight: 70, maxWidth: '90%', objectFit: 'contain' }} />
                  ) : (
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>No logo</Typography>
                  )}
                </Box>
                <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 1 }}>
                  For light backgrounds, documents, and emails
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  size="sm"
                  fullWidth
                  startDecorator={uploadingLogoField === 'logo_square_dark' ? <CircularProgress size="sm" /> : <UploadIcon />}
                  component="label"
                  disabled={uploadingLogoField !== null}
                >
                  {uploadingLogoField === 'logo_square_dark' ? 'Uploading...' : 'Upload'}
                  <input type="file" hidden accept="image/*" disabled={uploadingLogoField !== null}
                    onChange={(e) => { const file = e.target.files?.[0]; if (file) handleLogoUpload(file, 'logo_square_dark'); }} />
                </Button>
              </Card>
            </Grid>
          </Grid>

          {/* Wide Logos */}
          <Typography level="title-md" sx={{ mb: 1 }}>Company Logos - Wide</Typography>
          <Typography level="body-sm" sx={{ mb: 2, color: 'text.tertiary' }}>
            Horizontal logos for headers, navigation bars, and email signatures.
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Wide Full-Color */}
            <Grid xs={12} md={4}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography level="title-sm" sx={{ mb: 1 }}>Full-Color</Typography>
                <Box sx={{
                  height: 60,
                  bgcolor: 'background.level1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 'sm',
                  mb: 1
                }}>
                  {formData.logo_wide_full_color ? (
                    <Box component="img" src={formData.logo_wide_full_color} alt="Wide Full-Color" sx={{ maxHeight: 50, maxWidth: '90%', objectFit: 'contain' }} />
                  ) : (
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>No logo</Typography>
                  )}
                </Box>
                <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 1 }}>
                  For headers and navigation bars
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  size="sm"
                  fullWidth
                  startDecorator={uploadingLogoField === 'logo_wide_full_color' ? <CircularProgress size="sm" /> : <UploadIcon />}
                  component="label"
                  disabled={uploadingLogoField !== null}
                >
                  {uploadingLogoField === 'logo_wide_full_color' ? 'Uploading...' : 'Upload'}
                  <input type="file" hidden accept="image/*" disabled={uploadingLogoField !== null}
                    onChange={(e) => { const file = e.target.files?.[0]; if (file) handleLogoUpload(file, 'logo_wide_full_color'); }} />
                </Button>
              </Card>
            </Grid>
            {/* Wide Light */}
            <Grid xs={12} md={4}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography level="title-sm" sx={{ mb: 1 }}>Light</Typography>
                <Box sx={{
                  height: 60,
                  bgcolor: 'neutral.800',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 'sm',
                  mb: 1
                }}>
                  {formData.logo_wide_light ? (
                    <Box component="img" src={formData.logo_wide_light} alt="Wide Light" sx={{ maxHeight: 50, maxWidth: '90%', objectFit: 'contain' }} />
                  ) : (
                    <Typography level="body-xs" sx={{ color: 'neutral.400' }}>No logo</Typography>
                  )}
                </Box>
                <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 1 }}>
                  For dark footers and backgrounds
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  size="sm"
                  fullWidth
                  startDecorator={uploadingLogoField === 'logo_wide_light' ? <CircularProgress size="sm" /> : <UploadIcon />}
                  component="label"
                  disabled={uploadingLogoField !== null}
                >
                  {uploadingLogoField === 'logo_wide_light' ? 'Uploading...' : 'Upload'}
                  <input type="file" hidden accept="image/*" disabled={uploadingLogoField !== null}
                    onChange={(e) => { const file = e.target.files?.[0]; if (file) handleLogoUpload(file, 'logo_wide_light'); }} />
                </Button>
              </Card>
            </Grid>
            {/* Wide Dark */}
            <Grid xs={12} md={4}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography level="title-sm" sx={{ mb: 1 }}>Dark</Typography>
                <Box sx={{
                  height: 60,
                  bgcolor: 'background.level1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 'sm',
                  mb: 1
                }}>
                  {formData.logo_wide_dark ? (
                    <Box component="img" src={formData.logo_wide_dark} alt="Wide Dark" sx={{ maxHeight: 50, maxWidth: '90%', objectFit: 'contain' }} />
                  ) : (
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>No logo</Typography>
                  )}
                </Box>
                <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 1 }}>
                  For light backgrounds and print
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  size="sm"
                  fullWidth
                  startDecorator={uploadingLogoField === 'logo_wide_dark' ? <CircularProgress size="sm" /> : <UploadIcon />}
                  component="label"
                  disabled={uploadingLogoField !== null}
                >
                  {uploadingLogoField === 'logo_wide_dark' ? 'Uploading...' : 'Upload'}
                  <input type="file" hidden accept="image/*" disabled={uploadingLogoField !== null}
                    onChange={(e) => { const file = e.target.files?.[0]; if (file) handleLogoUpload(file, 'logo_wide_dark'); }} />
                </Button>
              </Card>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Grid container spacing={2}>
            <Grid xs={12}>
              <FormControl id="company-field-overview" error={Boolean(errors.overview)} required={requiredOrganizationFields.has('overview')}>
                <FormLabel>Brand Overview</FormLabel>
                <Textarea
                  slotProps={{ textarea: { 'aria-invalid': Boolean(errors.overview) } }}
                  minRows={3}
                  value={brandingData.overview || ''}
                  onChange={(e) => handleBrandingChange('overview', e.target.value)}
                  placeholder="Describe your brand identity, values, and messaging..."
                />
                {(errors.overview) && <FormHelperText>{errors.overview}</FormHelperText>}
              </FormControl>
            </Grid>
            
            {/* Brand Colors */}
            <Grid xs={12}>
              <Typography level="title-md" sx={{ mb: 2, mt: 1 }}>Brand Colors</Typography>
            </Grid>
            <Grid xs={12} md={4} id="company-field-brand_color_1">
              <ColorPicker
                label="Primary Color (required for branding)"
                value={brandingData.brand_color_1 ? `#${brandingData.brand_color_1}` : ''}
                onChange={(color) => handleBrandingChange('brand_color_1', color)}
                helpText={errors.brand_color_1 || "Required when saving branding; not needed to update company information."}
              />
            </Grid>
            <Grid xs={12} md={4} id="company-field-brand_color_2">
              <ColorPicker
                label="Secondary Color"
                value={brandingData.brand_color_2 ? `#${brandingData.brand_color_2}` : ''}
                onChange={(color) => handleBrandingChange('brand_color_2', color)}
                helpText="Supporting brand color"
              />
            </Grid>
            <Grid xs={12} md={4} id="company-field-brand_color_3">
              <ColorPicker
                label="Accent Color 1"
                value={brandingData.brand_color_3 ? `#${brandingData.brand_color_3}` : ''}
                onChange={(color) => handleBrandingChange('brand_color_3', color)}
                helpText="Additional accent color"
              />
            </Grid>
            <Grid xs={12} md={4} id="company-field-brand_color_4">
              <ColorPicker
                label="Accent Color 2"
                value={brandingData.brand_color_4 ? `#${brandingData.brand_color_4}` : ''}
                onChange={(color) => handleBrandingChange('brand_color_4', color)}
                helpText="Optional accent color"
              />
            </Grid>
            <Grid xs={12} md={4} id="company-field-brand_color_5">
              <ColorPicker
                label="Accent Color 3"
                value={brandingData.brand_color_5 ? `#${brandingData.brand_color_5}` : ''}
                onChange={(color) => handleBrandingChange('brand_color_5', color)}
                helpText="Optional accent color"
              />
            </Grid>
            
            {/* Typefaces */}
            <Grid xs={12}>
              <Typography level="title-md" sx={{ mb: 2, mt: 3 }}>Typography</Typography>
            </Grid>
            <Grid xs={12} md={6} id="company-field-brand_typeface_1">
              <GoogleFontsSelect
                value={brandingData.brand_typeface_1 || ''}
                onChange={(value) => handleBrandingChange('brand_typeface_1', value)}
                label="Primary Typeface"
                placeholder="Select primary font"
              />
            </Grid>
            <Grid xs={12} md={6} id="company-field-brand_typeface_2">
              <GoogleFontsSelect
                value={brandingData.brand_typeface_2 || ''}
                onChange={(value) => handleBrandingChange('brand_typeface_2', value)}
                label="Secondary Typeface"
                placeholder="Select secondary font"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}><Button startDecorator={<SaveIcon />} onClick={handleSave} loading={updateOrgMutation.isPending || updateBrandingMutation.isPending}>Save company details</Button></Box>

      {/* Top Page Modal */}
      <TopPageModal
        open={topPageModalOpen}
        onClose={() => {
          setTopPageModalOpen(false);
          setEditingTopPage(null);
        }}
        topPage={editingTopPage}
        onSave={handleTopPageSave}
        loading={createTopPageMutation.isPending || updateTopPageMutation.isPending}
      />

      {/* Service Area Modal */}
      <ServiceAreaModal
        open={serviceAreaModalOpen}
        onClose={() => {
          setServiceAreaModalOpen(false);
          setEditingServiceArea(null);
        }}
        serviceArea={editingServiceArea}
        onSave={handleServiceAreaSave}
        loading={createServiceAreaMutation.isPending || updateServiceAreaMutation.isPending}
      />
    </Box>
  );
};
